import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CopyIcon from '@mui/icons-material/ContentCopy'

interface BrandFunnelSection {
  title: string
  content: string
  tablesUsed: string[]
  individualAnalyses: Record<string, any>
  metadata: any
}

interface BrandFunnelCategory {
  category: string
  categoryDisplayName: string
  pillarName: string
  pillarDescription: string
  sections: BrandFunnelSection[]
  metadata: {
    totalTables: number
    analysisCount: number
    generatedAt: string
  }
}

interface BrandFunnelReportData {
  metadata: {
    reportType: string
    title: string
    description: string
    generatedAt: string
    categoriesIncluded: number
    totalSections: number
    compiler: string
    version: string
  }
  categories: BrandFunnelCategory[]
  crossCategoryInsights?: {
    title: string
    content: string
    metadata: {
      type: string
      generatedAt: string
    }
  }
}

interface BrandFunnelReportProps {
  data: BrandFunnelReportData
  onCopy: (content: string) => void
  onOpenTable: (tableName: string) => void
}

export const BrandFunnelReport: React.FC<BrandFunnelReportProps> = ({
  data,
  onCopy,
  onOpenTable,
}) => {
  // Add defensive checks
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No Brand Funnel data available</Typography>
      </Box>
    )
  }

  if (!data.categories || !Array.isArray(data.categories)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Invalid Brand Funnel data structure: categories missing</Typography>
      </Box>
    )
  }

  const handleCopySection = (content: string) => {
    onCopy(content)
  }

  const handleCopyCategory = (category: BrandFunnelCategory) => {
    const categoryContent = category.sections?.map(section => 
      `## ${section.title}\n${section.content}`
    ).join('\n\n') || ''
    const fullContent = `# ${category.categoryDisplayName}\n\n${categoryContent}`
    onCopy(fullContent)
  }

  const formatContent = (content: string, sectionTitle?: string) => {
    if (!content) return null
    
    return content.split('\n').map((line, index) => {
      if (line.startsWith('## ')) {
        const headerText = line.replace('## ', '').trim()
        // Skip header if it matches the section title (case-insensitive)
        if (sectionTitle && headerText.toLowerCase() === sectionTitle.toLowerCase()) {
          return null
        }
        return (
          <Typography key={index} variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            {headerText}
          </Typography>
        )
      } else if (line.startsWith('• ') || line.startsWith('- ')) {
        return (
          <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
            {line}
          </Typography>
        )
      } else if (line.trim() === '') {
        return <Box key={index} sx={{ height: '0.5rem' }} />
      } else {
        return (
          <Typography key={index} variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
            {line}
          </Typography>
        )
      }
    }).filter(element => element !== null)
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* Header */}
      <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            bgcolor: 'info.main', 
            color: 'white', 
            p: 3,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {data.metadata.title}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {data.metadata.description}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {data.metadata.categoriesIncluded} Categories
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {data.metadata.totalSections} Sections
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Categories */}
      {data.categories.map((category, categoryIndex) => (
        <Paper key={category.category} elevation={1} sx={{ mb: 2 }}>
          <Accordion>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                bgcolor: 'grey.50',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {category.categoryDisplayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.sections?.length || 0} sections • {category.metadata?.totalTables || 0} tables
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  <Chip 
                    label={`${category.sections?.length || 0} sections`} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Tooltip title={`Copy ${category.categoryDisplayName} content`}>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCopyCategory(category)
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                {category.sections.map((section, sectionIndex) => (
                  <Box key={sectionIndex} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {section.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {section.tablesUsed.length > 0 && (
                          <Chip 
                            label={`${section.tablesUsed.length} tables`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        )}
                        <Tooltip title="Copy section content">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopySection(section.content)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      {formatContent(section.content, section.title)}
                    </Box>

                    {section.tablesUsed.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Tables Used:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {section.tablesUsed.map((tableName, tableIndex) => (
                            <Chip
                              key={tableIndex}
                              label={tableName}
                              size="small"
                              onClick={() => onOpenTable(tableName)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'primary.light', color: 'white' }
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {sectionIndex < category.sections.length - 1 && (
                      <Divider sx={{ mt: 2 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      ))}

      {/* Report Info */}
      <Paper elevation={1} sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          Generated: {new Date(data.metadata.generatedAt).toLocaleString()} • 
          Compiler: {data.metadata.compiler} v{data.metadata.version} • 
          Categories: {data.metadata.categoriesIncluded} • 
          Sections: {data.metadata.totalSections}
        </Typography>
      </Paper>
    </Box>
  )
}

export default BrandFunnelReport 